/**
 * Preferences and saved views: the shapes the server stores per account and the client
 * keeps a copy of (Phase 7, Lane O).
 *
 * Preferences change defaults and density only — nothing is gated behind them. The
 * server validates what it stores against this shape: unknown keys are dropped and a
 * value of the wrong type falls back to the default, so an older or newer client can
 * never write something another one misreads.
 */

export interface Prefs {
  /** row height and card size */
  density: 'comfortable' | 'compact';
  /** open the "Advanced" sections of forms by default */
  expandAdvanced: boolean;
  /** show ids, corpus paths, cache hits, build times */
  technicalDetails: boolean;
  /** exercise editors open on the source rather than the form */
  sourceFirst: boolean;
  /** the navigation rail shows icons only */
  railCollapsed: boolean;
  /** the interface language; absent: the browser's (Phase 7, Lane I) */
  locale?: 'en' | 'de';
}

export const DEFAULT_PREFS: Prefs = {
  density: 'comfortable',
  expandAdvanced: false,
  technicalDetails: false,
  sourceFirst: false,
  railCollapsed: false,
};

/** the stored JSON of one account's preferences may not grow past this */
export const PREFS_MAX_BYTES = 4096;

const ENUMS: Partial<Record<keyof Prefs, readonly string[]>> = {
  density: ['comfortable', 'compact'],
  locale: ['en', 'de'],
};
const BOOLEANS: (keyof Prefs)[] = ['expandAdvanced', 'technicalDetails', 'sourceFirst', 'railCollapsed'];

/**
 * Keep what is a known preference with a value of the right type; drop everything else.
 * Returns only the keys that were valid, so a partial update stays partial.
 */
export function sanitizePrefs(raw: unknown): Partial<Prefs> {
  const out: Partial<Prefs> = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  const source = raw as Record<string, unknown>;
  for (const [key, allowed] of Object.entries(ENUMS) as [keyof Prefs, readonly string[]][]) {
    const value = source[key];
    if (typeof value === 'string' && allowed.includes(value)) (out as Record<string, unknown>)[key] = value;
  }
  for (const key of BOOLEANS) {
    if (typeof source[key] === 'boolean') (out as Record<string, unknown>)[key] = source[key];
  }
  return out;
}

/** What `GET /api/prefs` answers; `updatedAt` null: nothing stored for this account yet. */
export interface PrefsResponse {
  prefs: Partial<Prefs>;
  updatedAt: string | null;
}

/* ------------------------------------------------------------- saved views */

/** Where a saved view applies: Find exercises, or the Sheets page. */
export type SavedViewScope = 'library' | 'sheets';
export const SAVED_VIEW_SCOPES: readonly SavedViewScope[] = ['library', 'sheets'];

/**
 * A named filter set. `query` is the page's URL query string without the `?` (Find
 * exercises: its filters as libraryUrl.ts writes them; Sheets: `view`, `term`, `q`,
 * `mine`). Shared views are listed for every signed-in user; only their owner or an
 * admin changes them.
 */
export interface SavedView {
  id: number;
  scope: SavedViewScope;
  name: string;
  query: string;
  shared: boolean;
  ownerId: number | null;
  ownerName: string | null;
  createdAt: string;
  /** whether the caller may rename, share or delete it */
  mine: boolean;
}

export const SAVED_VIEW_NAME_MAX = 80;
export const SAVED_VIEW_QUERY_MAX = 2000;
/** per account and scope — a guard against a script, not a limit anyone will meet */
export const SAVED_VIEWS_PER_SCOPE = 100;

/** A view's name, trimmed and single-line, or an error message. */
export function validateViewName(raw: unknown): string | { error: string } {
  if (typeof raw !== 'string') return { error: 'A view needs a name.' };
  const name = raw.replace(/\s+/g, ' ').trim();
  if (!name) return { error: 'A view needs a name.' };
  if (name.length > SAVED_VIEW_NAME_MAX) return { error: `Keep the name under ${SAVED_VIEW_NAME_MAX} characters.` };
  return name;
}

/** A view's query string: no leading `?`, parseable, within the size limit. */
export function validateViewQuery(raw: unknown): string | { error: string } {
  if (typeof raw !== 'string') return { error: 'A view needs its filters as a query string.' };
  const query = raw.trim().replace(/^\?/, '');
  if (query.length > SAVED_VIEW_QUERY_MAX) return { error: 'Those filters are too long to save.' };
  if (/[\u0000-\u001f#]/.test(query)) return { error: 'That is not a query string.' };
  return query;
}
